import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  addZadUnpaidFollowupContact,
  addZadUnpaidFollowupPromise,
  closeZadUnpaidFollowup,
  createZadUnpaidFollowup,
  fetchMyZadUnpaidFollowupPermission,
  fetchZadUnpaidFollowupById,
  fetchZadUnpaidFollowupCandidates,
  fetchZadUnpaidFollowupDossiers,
  fetchZadUnpaidFollowupPermissionAudit,
  fetchZadUnpaidFollowupPermissions,
  fetchZadUnpaidFollowupSummary,
  linkZadUnpaidFollowupSale,
  lookupZadUnpaidFollowupTicket,
  postZadUnpaidFollowupReminder,
  reopenZadUnpaidFollowup,
  searchZadUnpaidMaarefatiUsers,
  setZadUnpaidFollowupPermission,
  unlinkZadUnpaidFollowupSale,
  updateZadUnpaidFollowup,
} from './service'
import {ZadUnpaidFollowupState} from './types'

const initialState: ZadUnpaidFollowupState = {
  me: null,
  meLoading: false,
  meInitialized: false,
  meFailed: false,
  summary: null,
  summaryLoading: false,
  summaryError: null,
  summaryErrorCode: null,
  candidates: [],
  candidateTotal: 0,
  candidatePages: 0,
  candidateScanCap: 2000,
  candidateScanned: 0,
  candidateLoading: false,
  dossiers: [],
  dossierTotal: 0,
  dossierPages: 0,
  dossierLoading: false,
  selected: null,
  detailLoading: false,
  ticket: null,
  ticketLoading: false,
  maarefatiUsers: [],
  maarefatiLoading: false,
  users: [],
  audits: [],
  listLoading: false,
  saveLoading: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadUnpaidFollowupState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function applyDossier(state: ZadUnpaidFollowupState, action: PayloadAction<any>) {
  state.actionLoading = false
  if (action.payload?.dossier) state.selected = action.payload.dossier
}

const zadUnpaidFollowupSlice = createSlice({
  name: 'zadUnpaidFollowups',
  initialState,
  reducers: {
    clearZadUnpaidFollowupError: (state) => {
      state.error = null
      state.errorCode = null
      state.errorStatus = null
    },
    clearZadUnpaidFollowupTicket: (state) => {
      state.ticket = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMyZadUnpaidFollowupPermission.pending, (state) => {
      state.meLoading = true
      state.meFailed = false
    })
    builder.addCase(fetchMyZadUnpaidFollowupPermission.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.meLoading = false
      state.meInitialized = true
      state.meFailed = true
      state.me = null
    })
    builder.addCase(fetchMyZadUnpaidFollowupPermission.fulfilled, (state, action) => {
      state.meLoading = false
      state.meInitialized = true
      state.meFailed = false
      state.me = action.payload.me
    })

    builder.addCase(fetchZadUnpaidFollowupSummary.pending, (state) => {
      state.summaryLoading = true
      state.summaryError = null
    })
    builder.addCase(fetchZadUnpaidFollowupSummary.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.summaryLoading = false
      state.summaryError = (action.payload as ZadApiError)?.error || 'Request failed'
      state.summaryErrorCode = (action.payload as ZadApiError)?.errorCode || null
    })
    builder.addCase(fetchZadUnpaidFollowupSummary.fulfilled, (state, action) => {
      state.summaryLoading = false
      state.summary = action.payload.summary
    })

    builder.addCase(fetchZadUnpaidFollowupCandidates.pending, (state) => {
      state.candidateLoading = true
    })
    builder.addCase(fetchZadUnpaidFollowupCandidates.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.candidateLoading = false
    })
    builder.addCase(fetchZadUnpaidFollowupCandidates.fulfilled, (state, action) => {
      state.candidateLoading = false
      state.candidates = action.payload.candidates
      state.candidateTotal = action.payload.total
      state.candidatePages = action.payload.totalPages
      state.candidateScanCap = action.payload.scanCap
      state.candidateScanned = action.payload.scanned
    })

    builder.addCase(lookupZadUnpaidFollowupTicket.pending, (state) => {
      state.ticketLoading = true
    })
    builder.addCase(lookupZadUnpaidFollowupTicket.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.ticketLoading = false
      state.ticket = null
    })
    builder.addCase(lookupZadUnpaidFollowupTicket.fulfilled, (state, action) => {
      state.ticketLoading = false
      state.ticket = action.payload.ticket
    })

    builder.addCase(searchZadUnpaidMaarefatiUsers.pending, (state) => {
      state.maarefatiLoading = true
    })
    builder.addCase(searchZadUnpaidMaarefatiUsers.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.maarefatiLoading = false
    })
    builder.addCase(searchZadUnpaidMaarefatiUsers.fulfilled, (state, action) => {
      state.maarefatiLoading = false
      state.maarefatiUsers = action.payload.users
    })

    builder.addCase(fetchZadUnpaidFollowupDossiers.pending, (state) => {
      state.dossierLoading = true
    })
    builder.addCase(fetchZadUnpaidFollowupDossiers.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.dossierLoading = false
    })
    builder.addCase(fetchZadUnpaidFollowupDossiers.fulfilled, (state, action) => {
      state.dossierLoading = false
      state.dossiers = action.payload.dossiers
      state.dossierTotal = action.payload.total
      state.dossierPages = action.payload.totalPages
    })

    builder.addCase(fetchZadUnpaidFollowupById.pending, (state) => {
      state.detailLoading = true
    })
    builder.addCase(fetchZadUnpaidFollowupById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.detailLoading = false
    })
    builder.addCase(fetchZadUnpaidFollowupById.fulfilled, (state, action) => {
      state.detailLoading = false
      state.selected = action.payload.dossier
    })

    const pendingAction = (state: ZadUnpaidFollowupState) => {
      state.actionLoading = true
    }
    const rejectAction = (state: ZadUnpaidFollowupState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    }
    ;[
      createZadUnpaidFollowup,
      updateZadUnpaidFollowup,
      linkZadUnpaidFollowupSale,
      unlinkZadUnpaidFollowupSale,
      addZadUnpaidFollowupPromise,
      addZadUnpaidFollowupContact,
      postZadUnpaidFollowupReminder,
      closeZadUnpaidFollowup,
      reopenZadUnpaidFollowup,
    ].forEach((thunk) => {
      builder.addCase(thunk.pending, pendingAction)
      builder.addCase(thunk.rejected, rejectAction)
      builder.addCase(thunk.fulfilled, applyDossier)
    })

    builder.addCase(fetchZadUnpaidFollowupPermissions.pending, (state) => {
      state.listLoading = true
    })
    builder.addCase(fetchZadUnpaidFollowupPermissions.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
    })
    builder.addCase(fetchZadUnpaidFollowupPermissions.fulfilled, (state, action) => {
      state.listLoading = false
      state.users = action.payload.users
    })
    builder.addCase(setZadUnpaidFollowupPermission.pending, (state) => {
      state.saveLoading = true
    })
    builder.addCase(setZadUnpaidFollowupPermission.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.saveLoading = false
    })
    builder.addCase(setZadUnpaidFollowupPermission.fulfilled, (state) => {
      state.saveLoading = false
    })
    builder.addCase(fetchZadUnpaidFollowupPermissionAudit.fulfilled, (state, action) => {
      state.audits = action.payload.audits
    })
  },
})

export default zadUnpaidFollowupSlice.reducer
export const actions = zadUnpaidFollowupSlice.actions
export {initialState as zadUnpaidFollowupInitialState}
